import { ArrowUpRight } from "lucide-react";

const programs = [
  {
    title: "Track & Field",
    eyebrow: "Speed · Power · Sprint Mechanics",
    accent: "#FFD60A",
    emoji: "⚡",
    img: "https://images.pexels.com/photos/13159254/pexels-photo-13159254.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=900&w=1200",
    bullets: [
      "Sprint development",
      "Acceleration",
      "Speed endurance",
      "Strength & power",
      "Mobility & recovery",
    ],
  },
  {
    title: "Boxing",
    eyebrow: "Conditioning · Footwork · Rotational Power",
    accent: "#DC143C",
    emoji: "🥊",
    img: "https://images.unsplash.com/photo-1545191050-56ac1b870f51?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200",
    bullets: [
      "Conditioning",
      "Footwork",
      "Rotational power",
      "Shoulder endurance",
      "Core strength & durability",
    ],
  },
  {
    title: "Basketball",
    eyebrow: "Vertical · Lateral Quickness · Deceleration",
    accent: "#FF7A00",
    emoji: "🏀",
    img: "https://images.pexels.com/photos/30050102/pexels-photo-30050102.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=900&w=1200",
    bullets: [
      "Jump development",
      "Lateral quickness",
      "First-step explosiveness",
      "Deceleration",
      "Strength & conditioning",
    ],
  },
  {
    title: "Strength & Conditioning",
    eyebrow: "Periodized Strength · Hypertrophy · Performance",
    accent: "#1FA2FF",
    emoji: "💪",
    img: "https://images.pexels.com/photos/20901478/pexels-photo-20901478.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=900&w=1200",
    bullets: [
      "Strength",
      "Hypertrophy",
      "Conditioning",
      "Athletic development",
      "Core training",
    ],
  },
  {
    title: "General Fitness",
    eyebrow: "Fat Loss · Muscle Tone · Healthy Lifestyle",
    accent: "#10B981",
    emoji: "🏋️",
    img: "https://images.pexels.com/photos/4753890/pexels-photo-4753890.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=900&w=1200",
    bullets: [
      "Fat loss",
      "Muscle tone",
      "Beginner training",
      "Mobility",
      "Consistency & healthy movement",
    ],
  },
  {
    title: "Youth Athletes",
    eyebrow: "Long-Term Athletic Development",
    accent: "#06B6D4",
    emoji: "🚀",
    img: "https://images.pexels.com/photos/2294400/pexels-photo-2294400.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=900&w=1200",
    bullets: [
      "Movement literacy",
      "Speed & agility foundations",
      "Strength development",
      "Coordination",
      "Confidence & consistency",
    ],
  },
];

export default function ProgramPathsSection() {
  return (
    <section
      id="programs"
      className="relative nfp-section bg-[#070707]"
      data-testid="section-programs"
    >
      <div className="max-w-7xl mx-auto px-6 md:px-10">
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-12">
          <div>
            <p className="text-[11px] tracking-[0.3em] uppercase text-[#1FA2FF] mb-4">
              Program Paths · 250+ Programs
            </p>
            <h2 className="nfp-heading text-white text-4xl md:text-6xl leading-[1] tracking-tight">
              Pick your sport. <br />
              Pick your <span className="text-white/30">path.</span>
            </h2>
          </div>
          <p className="md:max-w-sm text-white/60 leading-relaxed">
            Six focused paths — each built by sport, with progressive
            programming from your first session to your peak.
          </p>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
          {programs.map((p) => (
            <article
              key={p.title}
              data-testid={`program-card-${p.title.toLowerCase().replace(/&/g, "").replace(/\s+/g, "-").replace(/-+/g, "-")}`}
              className="group relative overflow-hidden rounded-2xl border border-white/10 bg-[#101010] aspect-[4/5] transition-all duration-500"
              style={{
                boxShadow: `inset 0 0 0 1px transparent`,
              }}
            >
              <img
                src={p.img}
                alt={p.title}
                className="absolute inset-0 w-full h-full object-cover opacity-55 group-hover:opacity-80 group-hover:scale-105 transition-all duration-700"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black via-black/65 to-black/10" />
              <div
                className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-500 pointer-events-none"
                style={{
                  background: `radial-gradient(60% 40% at 50% 100%, ${p.accent}22 0%, transparent 70%)`,
                }}
              />
              <div className="absolute top-5 left-5 right-5 flex items-start justify-between z-10">
                <span
                  className="inline-flex items-center justify-center w-11 h-11 rounded-xl text-2xl border backdrop-blur-sm"
                  style={{
                    background: `${p.accent}1A`,
                    borderColor: `${p.accent}55`,
                  }}
                  aria-hidden="true"
                >
                  {p.emoji}
                </span>
                <span
                  className="w-9 h-9 rounded-full border flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity"
                  style={{ borderColor: `${p.accent}80`, color: p.accent }}
                >
                  <ArrowUpRight className="w-4 h-4" />
                </span>
              </div>
              <div className="absolute inset-0 p-6 flex flex-col justify-end">
                <span
                  className="text-[10px] tracking-[0.22em] uppercase font-bold"
                  style={{ color: p.accent }}
                >
                  {p.eyebrow}
                </span>
                <h3 className="nfp-heading text-white text-3xl md:text-4xl mt-2 tracking-tight">
                  {p.title}
                </h3>
                <ul className="mt-3 space-y-1 text-sm text-white/75">
                  {p.bullets.map((b) => (
                    <li key={b} className="flex items-center gap-2">
                      <span
                        className="w-1.5 h-1.5 rounded-full"
                        style={{ background: p.accent }}
                      />{" "}
                      {b}
                    </li>
                  ))}
                </ul>
              </div>
            </article>
          ))}
        </div>
      </div>
    </section>
  );
}
